// #region Types

/**
 * @typedef {Object} Product
 * @property {string} hrefFragment
 * @property {number} index
 * @property {number} quantity
 * @property {boolean?} quantityExceeded
 * @property {number} price
 * @property {number} ticket
 * @property {boolean?} numbersExceeded
 */

/**
 * @typedef {Object} Donation
 * @property {string} hrefFragment
 * @property {number} price
 * @property {number} quantity
 */

// #endregion

// #region Config

class Config {
    static tsParticleConfig = {
        id: "tsparticles",
        options: {
            fullScreen: {
                zIndex: 999,
                enable: false,
            },
            particles: {
                color: {
                    value: [
                        "#FFd700"
                    ]
                },
                move: {
                    direction: "bottom",
                    enable: true,
                    outModes: {
                        default: "out"
                    },
                    size: true,
                    speed: {
                        min: 1,
                        max: 2
                    }
                },
                number: {
                    value: 2000,
                    density: {
                        enable: true,
                        area: 800
                    }
                },
                opacity: {
                    value: 1,
                    animation: {
                        enable: false,
                        startValue: "max",
                        destroy: "min",
                        speed: 0.3,
                        sync: true
                    }
                },
                rotate: {
                    value: {
                        min: 0,
                        max: 360
                    },
                    direction: "random",
                    move: true,
                    animation: {
                        enable: true,
                        speed: 60
                    }
                },
                tilt: {
                    direction: "random",
                    enable: true,
                    move: true,
                    value: {
                        min: 0,
                        max: 360
                    },
                    animation: {
                        enable: true,
                        speed: 60
                    }
                },
                shape: {
                    type: "triangle",
                    options: {}
                },
                size: {
                    value: {
                        min: 2,
                        max: 4
                    }
                },
                roll: {
                    darken: {
                        enable: true,
                        value: 30
                    },
                    enlighten: {
                        enable: true,
                        value: 30
                    },
                    enable: true,
                    speed: {
                        min: 15,
                        max: 25
                    }
                },
                wobble: {
                    distance: 30,
                    enable: true,
                    move: true,
                    speed: {
                        min: -15,
                        max: 15
                    }
                }
            }
        }
    }
}

// #endregion

// #region Utils
class Utils {

    // Fetch and Return Data from API
    static async fetchAPIData(api) {
        try {
            const response = await fetch(api);
            if (response.status !== 200) return null;
            const data = await response.json();
            return data;
        } catch (err) {
            return null;
        }
    }

    // Run Async Function and return Data
    static async asyncFunction(func) {
        try {
            const data = await func;
            return data;
        } catch (err) {
            return null;
        }
    }

    // Convert card code into its proper name (i.e. aces -> Ace of Spades)
    static cardCodeToName(code, pageIsFrench) {
        if (!code) return "";

        const cardSuitMapEn = {
            h: 'Hearts',
            s: 'Spades',
            c: 'Clubs',
            d: 'Diamonds',
        }

        const cardSuitMapFr = {
            h: 'cœur',
            s: 'pique',
            c: 'trèfle',
            d: 'carreau',
        }

        const cardCodeNameMapFr = {
            ace: 'l\'as',
            two: 'le deux',
            three: 'le trois',
            four: 'le quatre',
            five: 'le cinq',
            six: 'le six',
            seven: 'le sept',
            eight: 'le huit',
            nine: 'le neuf',
            ten: 'le dix',
            jack: 'le valet',
            queen: 'la reine',
            king: 'le roi',
        }

        const cardName = code.slice(0, code.length - 1);
        const cardCode = code.slice(code.length - 1, code.length);

        let cardCodeAsName;
        let formattedCardName;

        if (pageIsFrench) {
            cardCodeAsName = cardSuitMapFr[cardCode];
            formattedCardName = cardCodeNameMapFr[cardName];
        } else {
            cardCodeAsName = cardSuitMapEn[cardCode];
            formattedCardName = cardName.replace(/\b\w/g, function (char) { return char.toUpperCase() });
        }

        return `${formattedCardName} ${pageIsFrench ? 'de' : 'of'} ${cardCodeAsName}`;
    }

    // Prepend 0
    static prepend0(num) {
        let number = num;
        if (number % 1 !== 0) number = Math.abs(Math.trunc(number));
        return ("0" + number).slice(-2);
    }
    // Prepend 1 is used for the custom countdown clock to show more than two digits for the coutdown clock
    static prependZero(num) {
        let number = num;
        if (number % 1 !== 0) number = Math.abs(Math.trunc(number));
        if (number.toString().length == 1) {
            return ("0" + number);
        } else {
            return number;
        }
    }

    // Get Current Locale 
    static getCurrentLocale() {
        return document.documentElement.lang ? document.documentElement.lang : 'en';
    }

    static getCurrentCurrency() {
        const currency = document.documentElement.getAttribute('currency');
        return currency ? currency : 'CAD';
    }

    static localizeNumbers(value, isCurrency = false, maxDigits = 0, minDigits = 0) {
        const currency = Utils.getCurrentCurrency();
        const currencyOption = isCurrency ? { style: 'currency', currency, maximumFractionDigits: maxDigits, minimumFractionDigits: minDigits } : {};
        return new Intl.NumberFormat(Utils.getCurrentLocale(), currencyOption).format(value).replace(/[A-Za-z]/g, "");
    }

    /**
     * Determine if the page locale is specifically french or not
     * @returns {boolean}
     */
    static pageIsFrench() {
        const pageLocale = this.getCurrentLocale();
        return (pageLocale === "fr" || pageLocale.includes("fr")) ? true : false;
    }

    // Parse Prize Type
    static parsePrizeType(typeNum, isEarlyBird, separateEBandConLabel = true) {

        let message = '';

        switch (typeNum) {
            case 1:
                message = langGrand;
                break;
            case 2:
                message = separateEBandConLabel ? langCon : langEB;
                break;
            case 4:
                message = langJackpot;
                break;
            default:
                message = '';
        }

        if (isEarlyBird) {
            message = langEB;
        }

        return message;
    }

    /**
     * Sort Winners Data
     * 
     * Winners will be sorted differently depending on the data source.
     * Should the source be a CSV, it will simply reverse the winners data.
     * Else, it will reverse the winner based on the date.
     * 
     * @param {Array} winnersDataOrigin 
     * @returns 
     */

    static sortWinners(winnersDataOrigin) {

        if (guillotineWinnersFromCSV) {
            return [...winnersDataOrigin].reverse();
        }

        const orderedWinners = [...winnersDataOrigin].sort((a, b) => {
            const dateA = new Date(a.isoDrawDate);
            const dateB = new Date(b.isoDrawDate);

            if (dateB - dateA < 0) {
                return -1;
            } else if (dateB - dateA > 0) {
                return 1;
            } else {
                const prizeTypeA = a.prizeTypeId;
                const prizeTypeB = b.prizeTypeId;

                if (prizeTypeA && prizeTypeB) {
                    return prizeTypeB - prizeTypeA;
                } else {
                    const prizeTitleA = a.prizeTitle.toLowerCase();
                    const prizeTitleB = b.prizeTitle.toLowerCase();

                    return prizeTitleA === prizeTitleB ? 0 : prizeTitleA === "jackpot" ? -1 : 1;
                }
            }
        });

        return orderedWinners;
    }

    static createScript(properties) {
        const scriptEl = document.createElement('script');
        for (const key in properties) {
            scriptEl.setAttribute(key, properties[key]);
        }
        document.head.appendChild(scriptEl);
    }

    // necessary to get the correct card image URL on Shopify
    static replaceInString(string, targetString, replacementString) {
        return string.replace(targetString, replacementString);
    }

    /**
     * Compare two object by comparing their stringified output
     * @param {Object<string, any>} obj1 
     * @param {Object<string, any>} obj2 
     * @returns 
     */
    static compareObj(obj1, obj2) {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    }

    /**
     * Clone an object to remove reference to the original object
     * @param {Object<string, any>} obj 
     * @returns 
     */
    static cloneObj(obj) {
        return JSON.parse(JSON.stringify(obj));
    }
}
// #endregion

// #region Initialize Vue App
// Initialize Vue App under a controlled setting
async function initializeVueApp(passedOptions) {

    const defaultOptions = {
        mixins: [],
        beforeCreateCallback: () => { },
        mountedCallback: () => { },
        id: '#vue_app'
    }

    const options = { ...defaultOptions, ...passedOptions };

    let currentEventId = null;
    let donationEventId = null;
    let asyncDataConfig = {};

    let allEventsData;

    if (domainFragment) {
        allEventsData = await Utils.fetchAPIData(`${guillotineRootDomain}/getactiveevents`);
    }

    // if there are multiple active events, filter out the donation events
    if (allEventsData && allEventsData.length > 0) {
        const raffleEvents = allEventsData.filter(event => event.eventTypeId !== 3);
        const donationEvents = allEventsData.filter(event => event.eventTypeId === 3);

        if (raffleEvents.length > 0) {
            // set the current id to be the event number of the first non-donation event
            currentEventId = raffleEvents[0].eventNo;
        }

        if (donationEvents.length > 0) {
            // set the donation event id to be the event number of the first donation event
            donationEventId = donationEvents[0].eventNo;
        }
    }

    if (currentEventId) {
        asyncDataConfig.currentEventId = currentEventId;
    }

    if (donationEventId) {
        asyncDataConfig.donationEventId = donationEventId;
    }

    // Async Data to run first before initializing the app
    // Mimic Nuxt JS's Async Data behaviour in client side

    let asyncDataResult = {};

    await Promise.all([...options.mixins].map(async option => {
        if (option.asyncData) {
            const data = await Utils.asyncFunction(option.asyncData(asyncDataConfig));
            asyncDataResult = { ...asyncDataResult, ...data }
        }
    }));

    const mixinList = [...options.mixins].map(mixin => {
        if (mixin.asyncData)
            delete mixin.asyncData
        return mixin;
    });

    // Construct App Object with Data and Mixins implemented
    const CTA = {
        compilerOptions: {
            delimiters: ['[[', ']]'],
        },
        data: () => ({
            ...asyncDataResult,
        }),
        beforeCreate() {
            options.beforeCreateCallback(this, options);
        },
        mounted() {
            options.mountedCallback(this, options);
        },
        mixins: mixinList,
    }

    // Create Vue Instance
    const app = Vue.createApp(CTA);

    // Recognize every element starting with "afs-" to be a custom element
    app.config.isCustomElement = tag => tag.startsWith('afs-');

    // Mount App
    app.mount(options.id);

}
// #endregion

// #region Setup Mixin
const setupMixin = {
    async asyncData() {
        const raisedData = await Utils.fetchAPIData("https://ccbqel594l.execute-api.us-west-2.amazonaws.com/v1/3af28ebf/totaldrawamount");

        let clientConfigSettings = null;

        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].clientConfig) {
            clientConfigSettings = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].clientConfig;
        } else {
            clientConfigSettings = await Utils.fetchAPIData(`${guillotineRootDomain}/getclientconfig`);
        }

        return {
            raisedAmount: raisedData ? Utils.localizeNumbers(raisedData.total, true) : 0,
            clientConfigSettings: clientConfigSettings
        }
    },
    data() {
        return {
            isOntario: isOntario,
            domainFragment: domainFragment,
            ebLabelSeperationPrizeBox: ebLabelSeperationPrizeBox,
            ebLabelSeperationWinnerSection: ebLabelSeperationWinnerSection,
            ebCountdownToEb: ebCountdownToEb,
            dateTime: null,
            year: new Date().getFullYear(),
            clientConfigSettings: null,
        }
    },
    mounted() {
        const stpHeadline = document.querySelector('#headline-section.new-stp-headline');
        const floatingButton = document.querySelector('afs-stp-float-buy-button');

        // Price point section ID is currently the same for the STP headline and the price point section
        // If both are active (realistically never), it will prioritize the first one found
        const ppSection = document.querySelector("#price-point-buttons-section");

        // Only runs when the new STP headline is enabled and all elements exist
        if (stpHeadline && ppSection && floatingButton) {

            const fullyShowOnDesktop = ppSection.tagName === "AFS-SECTION";

            floatingButton.style.display = this.shouldShowOrHide(ppSection, fullyShowOnDesktop);
            window.addEventListener('scroll', () => {
                floatingButton.style.display = this.shouldShowOrHide(ppSection, fullyShowOnDesktop);
            });
            window.addEventListener('resize', () => {
                floatingButton.style.display = this.shouldShowOrHide(ppSection, fullyShowOnDesktop);
            });
        }
    },
    methods: {
        parsePrizeType: Utils.parsePrizeType,
        getCurrentLocale: Utils.getCurrentLocale,
        localizeNumbers: Utils.localizeNumbers,
        shouldShowOrHide: (targetEl, fullyShowOnDesktop = false, percentageShown = 0.5) => {
            if (window.innerWidth >= 768 && fullyShowOnDesktop) return "block";

            const targetRect = targetEl.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const targetVisibleHeight = Math.min(targetRect.bottom, windowHeight) - Math.max(targetRect.top, 0);
            const targetVisibleRatio = targetVisibleHeight / targetRect.height;

            return targetVisibleRatio >= percentageShown ? "none" : "block";
        }
    }
}
// #endregion

// #region Translate Mixin
const translateMixin = {
    data() {
        return {
            countdownFormat: langCountdownFormat,
            deadlineFormat: langDeadlineFormat,
            winnerDrawdateFormat: langWinnerDrawdateFormat,
            stpHeadlineJackpotSubtext: langStpHeadlineJackpotSubtext,
            pageIsFrench: Utils.pageIsFrench(),
        }
    },
    beforeCreate() {
        luxon.Settings.defaultLocale = Utils.getCurrentLocale();
    },
}
// #endregion

// #region CTA Setup Mixin
const ctaSetupMixin = {
    data() {
        return {
            isCtaPlayMessageBestValue
        }
    },
    computed: {
        isWinnersPage() {
            const pathName = window.location.pathname;
            return pathName.includes('winner');
        }
    },
    methods: {
        replaceInString: Utils.replaceInString
    }
}
// #endregion

// #region Prize Mixin
/**
 * Prize Mixin
 * 
 * Is responsible for the prizes displayed on the page
 * @mixin
 */
const prizeMixin = {
    async asyncData(dataConfig) {
        let prizeData = null;

        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].prizes) {
            prizeData = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].prizes;
        } else {
            prizeData = await Utils.fetchAPIData(`${guillotineRootDomain}/getprizes/${"currentEventId" in dataConfig ? dataConfig.currentEventId : ""}`);
        }

        return { prizeData: prizeData }
    },
    data() {
        return {
            prizeData: null,
        }
    },
    methods: {
        formatDeadlineDate(dateTimeString) {
            return luxon.DateTime.fromISO(dateTimeString).toFormat(this.deadlineFormat);
        },
        formatPrizeType(prize) {
            return Utils.parsePrizeType(prize.prizeTypeId, prize.isEarlyBird, ebLabelSeperationPrizeBox);
        },
        formatPrizeTitle(title, quantity) {
            return quantity && quantity > 1 ? `${title} x ${quantity}` : title;
        }
    },
    computed: {
        allEarlyBird() {
            if (!this.prizeData || !this.currentTime) return [];

            const otherPrizeList = [...this.prizeData].filter(prize => !prize.isEarlyBird && prize.prizeTypeId == 2);

            return [...this.allEarlyBirdWithDeadline, ...otherPrizeList];
        },
        allEarlyBirdWithDeadline() {
            if (!this.prizeData || !this.currentTime) return [];

            const ebList = [...this.prizeData]
                .filter(prize => prize.isEarlyBird && luxon.DateTime.fromISO(prize.deadline).diff(this.currentTime).toMillis() > 0)
                .sort((a, b) => {
                    return luxon.DateTime.fromISO(a.deadline) - luxon.DateTime.fromISO(b.deadline);
                });

            return ebList;
        }
    }
}
// #endregion

// #region Time Mixin
const timeEventMixin = {
    async asyncData(dataConfig) {
        let eventData = null;
        let currentTime = null;
        let eventEndTime = null;
        let eventStartTime = null;
        let eventNumber = null;
        let sellingClosed = null;
        let eventTitle = null;
        let salesEnd = null;

        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].event) {
            eventData = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].event;
        } else if ("currentEventId" in dataConfig) {
            eventData = await Utils.fetchAPIData(`${guillotineRootDomain}/getevent/${dataConfig.currentEventId}`);
        }

        const DateTime = luxon.DateTime;
        if (eventData) {
            eventNumber = eventData.eventNo;
            currentTime = useMockData ? DateTime.local() : DateTime.fromISO(eventData.currentTime);
            eventEndTime = DateTime.fromISO(eventData.salesEnd);
            eventStartTime = DateTime.fromISO(eventData.salesStart);
            sellingClosed = eventData.sellingClosed ? eventData.sellingClosed : false;
            eventTitle = eventData.title ? eventData.title : '';
            salesEnd = DateTime.fromISO(eventData.salesEnd);
        } else {
            currentTime = DateTime.local();
        }

        return {
            eventData: eventData,
            currentTime: currentTime,
            currentEndTime: eventEndTime,
            currentStartTime: eventStartTime,
            endTimesQueue: [eventEndTime],
            eventNumber: eventNumber,
            sellingClosed: sellingClosed,
            eventTitle: eventTitle,
            countdownClockFormat: eventData && eventData.countdownClockFormat ? eventData.countdownClockFormat : "",
            salesEnd: salesEnd,
        }
    },
    data() {
        return {
            eventData: null,
            currentTime: null,
            currentEndTime: null,
            currentStartTime: null,
            salesEnd: null,
            sellingClosed: null,
            timeStructure: ['weeks', 'days', 'hours', 'minutes', 'seconds'],
            timeStructureShort: ['days', 'hours', 'minutes', 'seconds'],
            timeLanguage: [langCountdownWeeks, langCountdownDays, langCountdownHours, langCountdownMin, langCountdownSec],
            eventInterval: null,
            endTimesQueue: [],
            globalCountdownFormatAllowedText: ['weeks', 'days', 'hours', 'minutes', 'seconds'],
            displayWinnerCountdownOffset: displayWinnerCountdownOffset,
            customCountdownClockFormat: [],
            errorMessage: '',
        }
    },
    async created() {
        if (!this.eventData) return;

        this.eventInterval = setInterval(() => {
            this.currentTime = this.currentTime.plus({ seconds: 1 });
            if (this.currentEndTime.diff(this.currentTime).toMillis() < 0) {

                this.endTimesQueue.shift();

                if (this.endTimesQueue.length >= 1) {
                    this.currentEndTime = this.endTimesQueue[0];
                }
            }
        }, 1000);

        if (this.eventIsHidden && !this.eventIsAboutToStart) return;

        if (this.ebCountdownToEb) {
            // Update endTimesQueue with early bird deadlines
            const earlyBirdDeadlines = this.allEarlyBirdWithDeadline.map(eb => luxon.DateTime.fromISO(eb.deadline));
            this.endTimesQueue = [...earlyBirdDeadlines, ...this.endTimesQueue];
        }

        if (this.eventIsAboutToStart) {
            this.endTimesQueue = [this.currentStartTime, ...this.endTimesQueue]
        }

        this.currentEndTime = this.endTimesQueue[0];
        if (this.eventData && this.eventData.countdownClockFormat) {
            this.parseCountdownFormat(this.eventData.countdownClockFormat);
        }
    },
    methods: {
        resetTime() {
            return {
                weeks: 0,
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
            }
        },
        prepend0: Utils.prepend0,
        prependZero: Utils.prependZero,
        getCountdownLabelName(structure) {
            const indexOfStructure = this.timeStructure.indexOf(structure);
            return this.timeLanguage[indexOfStructure];
        },
        parseCountdownFormat(format) {
            const regex = /\[\[(\w+)\]\]/gi; // Matches [[placeholder]]
            const placeholdersInFormat = [];
            let match;

            while ((match = regex.exec(format)) !== null) {
                const placeholder = match[1].toLowerCase();
                if (this.globalCountdownFormatAllowedText.includes(placeholder)) {
                    placeholdersInFormat.push(placeholder);
                } else {
                    this.errorMessage = `Invalid placeholder ${placeholder} in countdown format`;
                    console.error(this.errorMessage);
                    this.customCountdownClockFormat = [];
                    return;
                }
            }

            this.customCountdownClockFormat = this.globalCountdownFormatAllowedText.filter(placeholder => placeholdersInFormat.includes(placeholder));
            let reconstructedFormat = format;
            reconstructedFormat = reconstructedFormat.replace(regex, '[[PLACEHOLDER]]');
            const placeholdersReplacement = this.customCountdownClockFormat.map(placeholder => `[[${placeholder}]]`);

            let placeholdersIndex = 0;
            reconstructedFormat = reconstructedFormat.replace(/(\[\[PLACEHOLDER\]\])/g, () => {
                return placeholdersReplacement[placeholdersIndex++] || '';
            });

            this.countdownClockFormat = reconstructedFormat;
            return;
        },
        parseGlobalCountdownFormat(format) {
            return format.replace(/(\[\[.*?\]\])/g, (_, tag) => {
                const structure = tag.replace(/\[|\]/g, '').toLowerCase();
                return this.globalCountdownFormatAllowedText.includes(structure) ? this.prependZero((this.timeData[structure])) : tag;
            });
        },
        currentTimeIsWithinDateRange(rangeStart, rangeEnd) {
            const parsedStart = typeof rangeStart === "string" ? luxon.DateTime.fromISO(rangeStart) : rangeStart;
            const parsedEnd = typeof rangeEnd === "string" ? luxon.DateTime.fromISO(rangeEnd) : rangeEnd;

            return parsedStart.diff(this.currentTime).toMillis() <= 0 && parsedEnd.diff(this.currentTime).toMillis() > 0;
        }
    },
    computed: {
        eventIsHidden() {
            if (!this.eventData || this.sellingClosed) return true;
            return this.eventIsAboutToStart || this.currentEndTime.diff(this.currentTime).toMillis() <= 0;
        },
        eventIsAboutToStart() {
            if (!this.eventData) return false;
            return this.currentTime.diff(this.currentStartTime).toMillis() < 0;
        },
        isCountingDownEB() {
            if (this.endTimesQueue.length < 2) return false;
            return true;
        },
        timeDataShort() {
            if (!this.currentEndTime) return this.resetTime();
            const time = this.currentEndTime.diff(this.currentTime, this.timeStructureShort);
            if (time.seconds < 0) {
                return this.resetTime();
            }
            return time;
        },
        timeData() {
            if (!this.currentEndTime) return this.resetTime();
            let time = [];

            if (this.customCountdownClockFormat.length > 2) {
                time = this.currentEndTime.diff(this.currentTime, this.customCountdownClockFormat);
            } else {
                time = this.currentEndTime.diff(this.currentTime, this.timeStructure);
            }

            if (time.seconds < 0) {
                return this.resetTime();
            }
            return time;
        },
        previousTimeData() {
            if (!this.currentEndTime) return this.resetTime();
            const time = this.currentEndTime.diff(this.currentTime.minus({ seconds: 1 }), this.timeStructure);
            if (time.seconds < 0) {
                return this.resetTime();
            }
            return time;
        },
        comparedTimeData() {
            const timeData = this.timeData;
            const previousTimeData = this.previousTimeData;
            const differentTimeStructure = [];
            this.timeStructure.forEach(structure => {
                if (timeData[structure] !== previousTimeData[structure]) {
                    differentTimeStructure.push(structure);
                }
            });
            return differentTimeStructure;
        },
        currentEndDateTimeFormat() {
            if (!this.eventData) return;
            return this.currentEndTime.toFormat(this.countdownFormat);
        },
        salesEndDateTimeFormat() {
            if (!this.eventData) return;
            return this.salesEnd.toFormat(this.countdownFormat);
        },
        offsetEndTime() {
            if (!this.currentEndTime) return this.resetTime();
            const clonedEndTime = this.currentEndTime;
            return clonedEndTime.plus(luxon.Duration.fromObject({ hours: this.displayWinnerCountdownOffset }));
        },
        winnerCountdownTimeData() {
            if (!this.currentEndTime) return this.resetTime();
            const time = this.offsetEndTime.diff(this.currentTime, this.timeStructure);
            if (time.seconds < 0) {
                return this.resetTime();
            }
            return time;
        },
        previousWinnerCountdownTimeData() {
            if (!this.currentEndTime) return this.resetTime();
            const time = this.offsetEndTime.diff(this.currentTime.minus({ seconds: 1 }), ['days', 'hours', 'minutes', 'seconds']);
            if (time.seconds < 0) {
                return this.resetTime();
            }
            return time;
        },
        toDisplayWinnerFromCountdown() {
            if (!this.currentEndTime) return false;
            const time = this.offsetEndTime.diff(this.currentTime);
            return time.as("seconds") < 1;
        },
        salesEndHasPassed() {
            if (!this.currentTime || !this.currentEndTime) return true;
            return this.currentTime.diff(this.currentEndTime).as("milliseconds") >= 0;
        },
    },
    watch: {
        currentTime() {
            const comparedTimeStructure = this.comparedTimeData;
            if (this.eventIsHidden) return;

            for (const structure of comparedTimeStructure) {
                if (!this.$refs[structure]) continue;
                const currentStructureEl = this.$refs[structure][0];
                currentStructureEl.classList.remove('flip');
                setTimeout(() => currentStructureEl.classList.add('flip'), 50);
            }
        }
    }
}
// #endregion

// #region Tier Mixin
const tierMixin = {
    async asyncData(dataConfig) {
        let fetchedTierData = null;
        let fetchedDonationTierData = null;
        let fetchedGeolocationSettings = null;

        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pricePoints) {
            fetchedTierData = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pricePoints;
        } else {
            fetchedTierData = await Utils.fetchAPIData(`${guillotineRootDomain}/getpricepoints/${"currentEventId" in dataConfig ? dataConfig.currentEventId : ""}`);
        }

        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].donationPricePoints) {
            fetchedDonationTierData = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].donationPricePoints;
        } else if ("donationEventId" in dataConfig) {
            fetchedDonationTierData = await Utils.fetchAPIData(`${guillotineRootDomain}/getpricepoints/${dataConfig.donationEventId}`);
        }

        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].geolocationSettings) {
            fetchedGeolocationSettings = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].geolocationSettings;
        } else {
            fetchedGeolocationSettings = await Utils.fetchAPIData(`${guillotineRootDomain}/getgeolocationsettings`);
        }

        /** 
         * Initial check to compare guillotine price points with Shopify products
         * Return price points if they are not found on Shopify (this accounts for smart price points that
         * are not currently active), or if they are found, and available
         * (i.e. in stock, if tracking quantity)
         * */ 
        if (window.Shopify && shopifySourcedPricePoints && shopifySourcedPricePoints.length > 0) {          
            fetchedTierData = [...fetchedTierData].filter(tier => {
                const foundShopifyPricePoint = shopifySourcedPricePoints.find(pricePoint => pricePoint.variants[0].id === tier.shopifyVariantId);
                return !foundShopifyPricePoint || foundShopifyPricePoint.variants[0].available;
            });
        }

        return {
            pricePointData: fetchedTierData,
            donationPricePointData: fetchedDonationTierData,
            geolocationData: fetchedGeolocationSettings,
        }
    },
    data() {
        return {
            domainFragment: domainFragment,
            shopifyDomainFragment: shopifyDomainFragment,
            cartIsUpdating: false,
            defaultPricePointLimit: defaultPricePointLimit,
            buyButtons: [],
            pricePointButtons: {},
            shopifySourcedPricePoints: shopifySourcedPricePoints,
            donationPricePoints: donationPricePoints,
            pricePointImageList: pricePointImageList,
            pricePointLabelOption: pricePointLabelOption,
            pricePointLabelListEn: pricePointLabelListEn,
            pricePointLabelListFr: pricePointLabelListFr,
        }
    },
    mounted() {
        if (this.eventIsHidden) return;

        let initialPricePointState;
        window.addEventListener("cartInitialized", (e) => {
            initialPricePointState = Utils.cloneObj(e.detail.products);
        });

        Object.values(this.pricePointButtons).forEach(button => {
            button.addEventListener('addQuantity', (e) => {
                window.Shopify && Shopify.analytics.publish("add_quantity", {
                    price: e.detail.price,
                    ticket: e.detail.ticket,
                    quantity: e.detail.quantity,
                    variantId: e.detail.hrefFragment,
                    wasModified: e.detail.wasModified
                });
            });

            button.addEventListener('subtractQuantity', (e) => {
                window.Shopify && Shopify.analytics.publish("subtract_quantity", {
                    price: e.detail.price,
                    ticket: e.detail.ticket,
                    quantity: e.detail.quantity,
                    variantId: e.detail.hrefFragment,
                    wasModified: e.detail.wasModified
                });
            });
        });

        this.buyButtons.forEach(buyButton => {
            if (!buyButton) return;
            buyButton.addEventListener('buyButtonTriggered', async (e) => {
                if (this.cartIsUpdating) return;

                this.cartIsUpdating = true;

                /**@type {(Product|Donation)[]} */
                let cartProducts = e.detail.products;

                if (this.$refs?.prompt && !Utils.compareObj(initialPricePointState, cartProducts)) {
                    const productToUpsell = this.parseProductToUpsell(e.detail.products);

                    if (
                        (productToUpsell && upsellQueryParamEnabled && window.location.search.includes("ue"))
                        || (productToUpsell && !upsellQueryParamEnabled)
                    ) {
                        window.Shopify && Shopify.analytics.publish("upsell_prompted", { price: productToUpsell.price, ticket: productToUpsell.ticket, variantId: productToUpsell.hrefFragment });
                        const wantToUpgrade = await this.promptUpsell(productToUpsell);

                        if (wantToUpgrade) {
                            window.Shopify && Shopify.analytics.publish("upsell_accepted", { price: productToUpsell.price, ticket: productToUpsell.ticket, variantId: productToUpsell.hrefFragment });
                            this.updateCartForUpsell(productToUpsell);
                            const updatedCartDetails = await buyButton.getCartDetails();
                            cartProducts = updatedCartDetails.products;
                        } else {
                            window.Shopify && Shopify.analytics.publish("upsell_rejected");
                        }
                    }
                }

                let cardNumber = e.detail.cardNumber ? e.detail.cardNumber : null;

                if (window.Shopify) {
                    await this.purchaseProducts(cartProducts, cardNumber);
                } else {
                    console.log("Purchase will only run in Shopify");
                }

                this.cartIsUpdating = false;
            });
        })
    },
    computed: {
        isShopify() {
            if (this.filteredPricePointData.length < 1) return;
            const filteredShopifyPP = this.filteredPricePointData.filter(pricePoint => pricePoint.shopifyVariantId);
            return filteredShopifyPP.length > 0;
        },
        filteredPricePointData() {
            if (!this.pricePointData) return [];

            // filter out price points if display property is false or if price is 0
            const displayedPricePoints = [...this.pricePointData].filter(pricePoint => pricePoint.display && pricePoint.price > 0);
            const filteredPricePoints = [];

            displayedPricePoints.forEach(pricePoint => {
                const availableByDefault = pricePoint.schedule?.availableByDefault;

                if (
                    availableByDefault == null ||
                    availableByDefault == undefined ||
                    (availableByDefault && pricePoint.schedule.exceptPeriods.length <= 0)
                ) {
                    filteredPricePoints.push(pricePoint);
                    return;
                }

                const availabilityArray = [];

                pricePoint.schedule?.exceptPeriods.forEach(period => {
                    const startTime = luxon.DateTime.fromISO(period.startTime);
                    const endTime = luxon.DateTime.fromISO(period.endTime);

                    if (availableByDefault) {
                        if (startTime.diff(this.currentTime).toMillis() > 0 || endTime.diff(this.currentTime).toMillis() < 0) {
                            availabilityArray.push(true);
                        } else {
                            availabilityArray.push(false);
                        }
                    } else {
                        if (startTime.diff(this.currentTime).toMillis() <= 0 && endTime.diff(this.currentTime).toMillis() > 0) {
                            availabilityArray.push(true);
                        } else {
                            availabilityArray.push(false);
                        }
                    }
                });

                if (availableByDefault && !availabilityArray.includes(false)) {
                    filteredPricePoints.push(pricePoint);
                } else if (!availableByDefault && availabilityArray.includes(true)) {
                    filteredPricePoints.push(pricePoint);
                }
            });

            return filteredPricePoints;
        },
        pricePointLimit() {
            if (!this.eventData) return this.defaultPricePointLimit;
            if (!("maxTicketsForOnline" in this.eventData)) return this.defaultPricePointLimit;

            return this.eventData.maxTicketsForOnline;
        },
        sortedPricePointData() {
            return [...this.filteredPricePointData].map(tier => tier.price).sort((a, b) => a - b);
        },
        cheapestPrice() {
            return this.sortedPricePointData.length > 0 ? this.sortedPricePointData[0] : null;
        },
        bestValue() {
            return this.sortedPricePointData.length > 0 ? this.sortedPricePointData[this.sortedPricePointData.length - 1] : null;
        },
    },
    methods: {
        async purchaseSingleItem(hrefFragment) {
            if (this.cartIsUpdating) return;

            this.cartIsUpdating = true;
            await this.purchaseProducts([{ hrefFragment: hrefFragment, quantity: 1 }]);
            this.cartIsUpdating = false;
        },
        async purchaseProducts(products, cardNumber = null) {
            try {
                const parsedProducts = products.map(product => {
                    return {
                        id: parseInt(product.hrefFragment),
                        quantity: product.quantity,
                    }
                });

                const cartData = {
                    items: parsedProducts,
                    attributes: {}
                }

                if (cardNumber) {
                    cartData.attributes.card_number = cardNumber;
                }

                // if the getCartAffiliateAttr method returns a value
                // set the returned value as a cart attribute, as well as the event number
                const cartAffiliateAttr = this.getCartAffiliateAttr();

                cartData.attributes.affiliate_param = !!cartAffiliateAttr ? cartAffiliateAttr : null;
                if (this.eventNumber) {
                    cartData.attributes.event_no = this.eventNumber;
                }

                // check if a Meta in-app browser is being used
                const userAgent = window.navigator.userAgent.toLowerCase();
                const isFacebook = userAgent.includes('fban') || userAgent.includes('fbav');
                const isInstagram = userAgent.includes('instagram');
                const isMetaInAppBrowser = isFacebook || isInstagram;

                // skip geolocation if a Meta in-app browser is being used
                if (this.geolocationData && this.geolocationData.enforced && this.geolocationData.htmlEnabled && !isMetaInAppBrowser) {
                    await (() => new Promise(res => {
                        navigator.geolocation.getCurrentPosition(
                            position => {
                                cartData.attributes.coords_lat = position.coords.latitude
                                cartData.attributes.coords_long = position.coords.longitude
                                res(true);
                            },
                            () => res(false),
                        )
                    }))();
                }

                await fetch('/cart/clear.js');
                await fetch('/cart/add.js', {
                    method: 'post',
                    headers: {
                        Accept: 'application/json, text/plain, */*',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(cartData),
                });
                window.location.href = `${Shopify.routes.root}checkout`;
            } catch (err) {
                console.warn('Purchase has failed: ' + err);
            }
        },
        getCartAffiliateAttr() {
            // the eventOrgConfig variable is defined in headcontent.template.php
            if (!eventOrgConfig) return;

            // get the query params from url
            const urlParams = new URLSearchParams(window.location.search);

            // return if affiliate link query param is not found
            if (!urlParams.has('olc')) return;

            const affiliateVal = urlParams.get('olc');
            const currentOrgConfig = eventOrgConfig.find(config => config.event_no === this.eventNumber);

            // return if there's no data in the eventOrgConfig corresponding to the current event number
            if (!currentOrgConfig) return;

            const organizations = currentOrgConfig.organizations;
            const matchedAffiliate = organizations.find(org => org.link_code === affiliateVal);

            if (!matchedAffiliate) return;

            const matchedAffiliateValue = matchedAffiliate.link_code;
            return matchedAffiliateValue;
        },
        /**
         * Is responsible for the price points displayed on the Shopify landing page
         * Active Shopify products will be the source of truth
         * Guillotine will provide supplementary information, i.e. display status
         * @param {number} id - the variant ID of the price point
         * 
         * If price point is NOT available in guillotine data, display it ()
         * Else, match the price point to the guillotine data and check display status
         */
        getPricePointDisplay(id) {
            const foundPricePoint = this.filteredPricePointData.find(pp => pp.shopifyVariantId === id);
            return !foundPricePoint ? true : foundPricePoint.display;
        },
        /**
         * Parses and returns a valid product to upsell. If there are no product to upsell,
         * it will return null. Any donations will be ignored.
         * @param {(Product|Donation)[]} products 
         */
        parseProductToUpsell(products) {
            if (!products || products.length <= 1) return null;

            const productsWithQuantity = products.filter(prod => "ticket" in prod && prod.quantity > 0);
            if (productsWithQuantity.length !== 1) return null;

            const currentTierIndex = productsWithQuantity[0];
            /** @type {Product?} */
            let nextTier = null;
            for (let i = 0; i < products.length; i++) {
                if (!("ticket" in products[i])) continue;
                if (products[i].price <= currentTierIndex.price) continue;
                if (nextTier !== null && products[i].price >= nextTier.price) continue;
                nextTier = products[i];
            }
            return nextTier;
        },
        /**
         * Prompts the user to upgrade to the product passed in. The prompt will require
         * a prompt custom element. This is the `afs-prompt` element in Ascend's custom elements.
         * @param {Product} product 
         * @param {*} promptEl The prompt element to display the upsell prompt
         * @returns {Promise<boolean>}
         */
        async promptUpsell(product, promptEl = this.$refs?.prompt) {
            if (!product || !promptEl) return;
            return await promptEl.prompt({
                langConfirm: langUpsellAccept.replace("__PRICE__", Utils.localizeNumbers(product.price, true)).replace("__TICKET__", product.ticket),
                langReject: langUpsellReject,
                heading: langUpsellMsg,
                src: contentUpsellImg,
                alt: contentUpsellImgAlt,
            });
        },
        /**
         * Updates the quantity of all price points to 0 except for the product to upsell
         * @param {Product} product 
         * @returns 
         */
        updateCartForUpsell(product) {
            if (!product) return;
            const cartData = [];
            for (const key in this.pricePointButtons) {
                const btn = this.pricePointButtons[key];
                btn.setQuantity(btn.price === product.price && btn.ticket === product.ticket ? 1 : 0);
                cartData.push(btn.getPricePointInfo());
            }
        },
        matchPricePointId(id) {
            return this.filteredPricePointData.find(pp => pp.shopifyVariantId === id);
        },
        getPricePointImage(numOfTicket, price) {
            if (!this.pricePointData) return;
            const pricePointImageObj = this.pricePointImageList.find(pricePoint => pricePoint['003_dynamic_pricepoints_tickets'] === numOfTicket.toString() && pricePoint['003_dynamic_pricepoints_price'] === price.toString());
            return pricePointImageObj ? pricePointImageObj['003_dynamic_pricepoints_image'].url : null;
        },
        getPricePerTicket(numOfTickets, price) {
            if (numOfTickets <= 0) return 0;
            const pricePerTicket = price / numOfTickets;
            return `${Utils.localizeNumbers(pricePerTicket, true, 2, 2)} / ${this.pageIsFrench ? 'billet' : 'ticket'}`;
        },
        getPricePointLabel(numOfTickets, price) {
            if (!this.pricePointData) return;
            let pricePointLabelObj = null;
 
            if (this.pageIsFrench) {
                pricePointLabelObj = this.pricePointLabelListFr.find(pricePoint => pricePoint['003_pricepoints_tickets_fr'] === numOfTickets.toString() && pricePoint['003_pricepoints_price_fr'] === price.toString());
            } else {
                pricePointLabelObj = this.pricePointLabelListEn.find(pricePoint => pricePoint['003_pricepoints_tickets'] === numOfTickets.toString() && pricePoint['003_pricepoints_price'] === price.toString());
            }
            
            if (!pricePointLabelObj) return null;

            const pricePointLabelText = this.pageIsFrench ? pricePointLabelObj['003_pricepoint_custom_label_text_fr'] : pricePointLabelObj['003_pricepoint_custom_label_text'];
            return pricePointLabelText ? pricePointLabelText : null;
        },
        handlePricePointLabels(numOfTickets, price, defaultLabel) {
            if (!this.pricePointData) return;
            if (pricePointLabelOption === 'priceperticket') {
                return this.getPricePerTicket(numOfTickets, price);
            } else if (pricePointLabelOption === 'custom') {
                return this.getPricePointLabel(numOfTickets, price);
            } else {
                return defaultLabel;
            }
        }
    }
}
// #endregion

// #region Jackpot Mixin
const jackpotMixin = {
    async asyncData(dataConfig) {
        let data = null;
        let currentEventId = null;

        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pot) {
            data = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pot;
        } else {
            currentEventId = dataConfig.currentEventId;
            data = await Utils.fetchAPIData(`${guillotineRootDomain}/getpot/${"currentEventId" in dataConfig ? dataConfig.currentEventId : ""}`);
        }

        const grossSales = data.grossSales;

        return {
            potData: data,
            recentJackpot: null,
            recentJackpotEventNo: null,
            currentEventId: currentEventId,
            grossSales: grossSales
        };
    },
    data() {
        return {
            showLastJackpot: showLastJackpot,
            recentJackpot: null,
            recentJackpotEventNo: null,
            seededProgressive: seededProgressive,
        }
    },
    async created() {
        if (!this.eventData || this.eventIsAboutToStart) {
            const recentJackpotData = await this.getRecentJackpot();
            this.recentJackpot = recentJackpotData.jackpot;
            this.recentJackpotEventNo = recentJackpotData.eventNo;
            return;
        };
        this.intervalGetPot();
    },
    computed: {
        potValue() {
            if (!this.eventData || this.eventIsAboutToStart) return this.recentJackpot ? this.recentJackpot : 0;
            return this.potData ? this.potData.currentEventPot : 0;
        },
        formattedPot() {
            return Utils.localizeNumbers(this.potValue, true);
        },
        guaranteeExceeded() {
            if (!this.eventData || !this.potData) return;
            if (this.eventData.guarantee === null) return true;

            const currentPayout = this.isOntario ? this.grossSales / 2 : this.grossSales;
            return currentPayout > this.eventData.guarantee;
        },
        ctaPotValue() {
            let currentEventPot = 0;
            let jackpot = 0;
            
            if (!this.eventData) {
                const recentWinnerWeeklyPrize = this.winnersData ? this.winnersData[1].prizeAmount : 0;
                const recentWinnerGrandPrize = this.winnersData ? Math.max(this.winnersData[0].prizeAmount, seededProgressive) : 0;
                const recentWinnerTotalPrize = recentWinnerWeeklyPrize + recentWinnerGrandPrize;
              
                currentEventPot = 0;
                jackpot = this.recentJackpot ? Math.max(this.recentJackpot, recentWinnerTotalPrize) : 0;
            } else {
                currentEventPot = this.potData.currentEventPot;
                jackpot = Math.max(this.potData.jackpot, seededProgressive);
            }
        
            return {
                currentEventPot: currentEventPot,
                jackpot: jackpot,
            }
        }
    },
    methods: {
        async getRecentJackpot() {
            const recentJackpotData = {jackpot: 0, eventNo: null};

            if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].recentJackpot) {
                recentJackpotData.jackpot = mock[Utils.getCurrentLocale()] ? mock[Utils.getCurrentLocale()].recentJackpot.jackpot : 0;
                recentJackpotData.eventNo = mock[Utils.getCurrentLocale()] ? mock[Utils.getCurrentLocale()].recentJackpot.eventNo : null;
            }
            try {
                const data = await Utils.fetchAPIData(`${guillotineRootDomain}/getrecentjackpot`);
                recentJackpotData.jackpot = data ? data.jackpot : 0;
                recentJackpotData.eventNo = data ? data.eventNo : null;
            } catch (err) {
                console.error(err);
            }
            return recentJackpotData;
        },
        async getPot(currentEventId = this.currentEventId) {
            if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pot) {
                return mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pot;
            }

            return await Utils.fetchAPIData(`${guillotineRootDomain}/getpot/${currentEventId}`);
        },
        async intervalGetPot() {
            const interval = setInterval(async () => {
                if (!this.eventData || this.eventIsHidden) return;
                const data = await this.getPot();
                this.potData = data;
                this.grossSales = data.grossSales;

                if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pot || !this.eventData || this.eventIsHidden) {
                    clearInterval(interval);
                }
            }, 60000);
        },
        updateWinnerPrizeValue(prizeVal, eventNo, prizeTitle) {
            if (
                !this.eventData
                && this.recentJackpotEventNo === eventNo
                && prizeTitle.toLowerCase() === 'jackpot'
            ) {
                return Math.max(prizeVal, this.seededProgressive);
            } else if (
                this.eventData
                && this.eventData.eventNo === eventNo + 1
                && prizeTitle.toLowerCase() === 'jackpot'
            ) {
                return Math.max(prizeVal, this.seededProgressive);
            }
            
            return prizeVal;
        }
    },
    watch: {
        eventIsAboutToStart(newVal, oldVal) {
            if (oldVal && !newVal) {
                this.intervalGetPot();
            }
        }
    }
}
// #endregion

// #region Winners Mixin
const winnersMixin = {
    async asyncData() {
        let data = null;
        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].recentWinners) {
            data = mock[Utils.getCurrentLocale()] && [...mock[Utils.getCurrentLocale()].recentWinners];
        } else {
            data = await Utils.fetchAPIData(`${guillotineRootDomain}/getrecentwinners`);
        }

        const sortedWinnersData = Utils.sortWinners(data);

        sortedWinnersData.map(winnerData => {
            Object.assign(winnerData, {
                currentPrizeFormat: Utils.parsePrizeType(winnerData.prizeTypeId, winnerData.isEarlyBird, ebLabelSeperationWinnerSection)
            })
        })

        return {
            winnersData: sortedWinnersData,
            winningCardCode: winningCardCode,
            winningCardCodeName: Utils.cardCodeToName(winningCardCode, Utils.pageIsFrench()),
            enableWinnerCountdownPage: enableWinnerCountdownPage,
            postCountdownOffset: postCountdownOffset
        }
    },
    created() {
        if (!this.winnersData) this.winnersData = [];
        this.winnersData.forEach(winner => {
            if (winner.name && winner.name == 'Name Name') {
                winner.name = null;
            }
        })
    },
    methods: {
        cardCodeToName: Utils.cardCodeToName,
        getWinnerDrawDate(winner) {
            return luxon.DateTime.fromISO(winner.isoDrawDate).toFormat(this.winnerDrawdateFormat);
        },
        getWinnerPrize(winner) {
            return winner.prizeAmount && winner.prizeAmount > 0 ? this.localizeNumbers(parseFloat(winner.prizeAmount), true) : winner.prizeTitle;
        },
        async refetchWinners() {
            try {
                let data = null;
                if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].recentWinners) {
                    data = mock[Utils.getCurrentLocale()] && [...mock[Utils.getCurrentLocale()].recentWinners];
                } else {
                    data = await Utils.fetchAPIData(`${guillotineRootDomain}/getrecentwinners`);
                }
        
                const sortedWinnersData = Utils.sortWinners(data);
        
                sortedWinnersData.map(winnerData => {
                    Object.assign(winnerData, {
                        currentPrizeFormat: Utils.parsePrizeType(winnerData.prizeTypeId, winnerData.isEarlyBird, ebLabelSeperationWinnerSection)
                    })
                })

                this.winnersData = sortedWinnersData;
            } catch (err) {
                console.log(err);
            }
        },
        formatWinnerName(winnerName) {
            if (!this.clientConfigSettings || !this.clientConfigSettings.winnerNameEnabled) return '';
            return winnerName ? winnerName : '-';
        }
    },
    computed: {
        latestWinners() {
            const DateTime = luxon.DateTime;
            let latestWinners = [...this.winnersData].filter(winner => {
                const currentWinnerDateTime = DateTime.fromISO(winner.isoDrawDate);
                const latestWinnerDateTime = DateTime.fromISO(this.winnersData[0].isoDrawDate);

                if (
                    currentWinnerDateTime.day == latestWinnerDateTime.day
                    && currentWinnerDateTime.month == latestWinnerDateTime.month
                    && currentWinnerDateTime.year == latestWinnerDateTime.year
                )
                    return true
                else
                    return false
            });
            return latestWinners;
        },
        latestWinner() {
            if (!this.winnersData || this.winnersData.length < 1) return null;
            let latestWinner = [...this.winnersData].shift();
            return latestWinner;
        },
        latestWinnersPrizeFormat() {
            return this.latestWinner.currentPrizeFormat;
        },
        latestWinnerIsGrandPrizeWinner() {
            if (!this.latestWinnerDataSrc) return false;
            return this.latestWinnerDataSrc.cardCode == this.winningCardCode;
        },
        currentEventWinner() {
            if (!this.eventData || !this.latestWinnerDataSrc) return;
            return this.eventData.eventNo === this.latestWinnerDataSrc.eventNo;
        },
        delayedLatestWinner() {
            if (!this.delayedWinnersData || this.delayedWinnersData.length < 1) return null;
            let latestWinner = [...this.delayedWinnersData].shift();
            return latestWinner;
        },
        delayedWinnersData() {
            if (!this.latestWinner) return [];
            if (this.postCountdownOffset <= 0 || !this.eventData) return this.winnersData;
            if (!this.currentTime || !this.currentEndTime || this.currentTime.diff(this.currentEndTime).as("milliseconds") < 0) return this.winnersData;

            const additionalOffsetEndTime = this.offsetEndTime.plus(luxon.Duration.fromObject({ hours: this.postCountdownOffset }));
            const shouldDisplayLatestWinner = this.currentTime.diff(additionalOffsetEndTime).as("milliseconds") >= 0;
            return shouldDisplayLatestWinner ? this.winnersData : [...this.winnersData].filter(winner => winner.eventNo !== this.eventData.eventNo);
        },
        allWinnersDataSrc() {
            let allWinnersDataSrc;

            if (this.enableWinnerCountdownPage) {
                allWinnersDataSrc = this.delayedWinnersData ? this.delayedWinnersData : [];
            } else {
                allWinnersDataSrc = this.winnersData ? this.winnersData : [];
            }

            return allWinnersDataSrc;
        },
        latestWinnerDataSrc() {
            let latestWinnerDataSrc;

            if (this.enableWinnerCountdownPage) {
                latestWinnerDataSrc = this.delayedLatestWinner ? this.delayedLatestWinner : null;
            } else {
                latestWinnerDataSrc = this.latestWinner ? this.latestWinner : null;
            }

            return latestWinnerDataSrc;
        }
    },
    watch: {
        toDisplayWinnerFromCountdown: {
            handler(newVal, oldVal) {
                if (
                    newVal === true 
                    && document.getElementById(Config.tsParticleConfig.id)
                    && document.getElementById(Config.tsParticleConfig.id).children.length < 1
                ) {
                    if ("tsParticles" in window) {
                        tsParticles.load(
                            Config.tsParticleConfig,
                        );
                    }
                    this.refetchWinners();
                }
            },
            deep: true,
        }
    }
}

// #endregion

// #region Card Tier Mixin
const cardDeckMixin = {
    async asyncData() {
        let fetchedCardDeckData = null;

        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].cardDeck) {
            fetchedCardDeckData = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].cardDeck;
        } else {
            fetchedCardDeckData = await Utils.fetchAPIData(`${guillotineRootDomain}/getcarddeck`);
        }

        return {
            cardData: fetchedCardDeckData,
            domainFragment,
            isCardBackImage,
            isCustomCardBackImage,
            isCardBackImageNumeral,
        }
    },
    methods: {
        isCardFlipped(currentCardIndex, revealedCards) {

            const filteredResult = [...revealedCards].filter(card => card.position == currentCardIndex);
            if (filteredResult.length > 0) {
                return {
                    img: filteredResult[0].code,
                    isFlipped: true,
                }
            }

            return {
                isFlipped: false,
            }

        },
        cardDataContainsWinningCard(winnerCardCode) {
            const revealedCards = this.cardData.revealedCards;
            return revealedCards.length < 1 ? false : revealedCards[revealedCards.length - 1].code == winnerCardCode;
        },
        /**
         * This method is responsible for parsing the correct card image source based on the card number,
         * whether the card has been flipped, as well as conditions set by the client, including:
         * whether a custom backface image has been enabled, and whether the images are numbered
         * @param {number} cardNum 
         * @param {string} backfaceUrl 
         * @param {string} customBackfaceUrl 
         * @param {string} numberedBackfaceUrl 
         * @param {string} placeholderUrl 
         * @param {boolen} isShopify 
         * @returns {string}
         */
        parseCorrectCard(cardNum, backfaceUrl, customBackfaceUrl, numberedBackfaceUrl, placeholderUrl, isShopify) {
            const placeholderImgName = 'placeholder.jpg'

            if (this.isCardFlipped(cardNum, this.cardData.revealedCards).isFlipped) {
                return isShopify
                    ? Utils.replaceInString(placeholderUrl, placeholderImgName, `${this.isCardFlipped(cardNum, this.cardData.revealedCards).img}.jpg`)
                    : placeholderUrl.replace(placeholderImgName, `${this.isCardFlipped(cardNum, this.cardData.revealedCards).img}.jpg`);
            }

            if (!this.isCardBackImage) return "";

            if (this.isCardBackImageNumeral) {
                
                if (isShopify) {
                    const fileExtension = JSON.parse(numberedBackfaceUrl)[cardNum - 1]?.['image'].match(/\.[0-9a-z]{1,5}$/i)[0];
                    return Utils.replaceInString(placeholderUrl, placeholderImgName, `backface${cardNum}${fileExtension}`);
                }

                return numberedBackfaceUrl[cardNum - 1]?.['image'] ?? '';
            }

            if (this.isCustomCardBackImage) return customBackfaceUrl;

            return backfaceUrl;
        }
    },
}
// #endregion

// #region Detailed Winners Mixin
const detailedWinnersMixin = {
    asyncData() {
        return {
            detailedWinnersData: detailedWinnersList.reverse(),
        }
    },
    methods: {
        limitedDetailedWinnersList(count = 0) {
            return this.detailedWinnersData.slice(0, count);
        },
        renderNameLocation(winner, pageIsFrench) {
            const name = pageIsFrench ? winner.name_fr : winner.name;
            const location = pageIsFrench ? winner.location_fr : winner.location;

            if (name !== "" && location !== "") {
                return name + ', ' + location;
            } else if (name !== "") {
                return name;
            } else if (location !== "") {
                return location;
            } else {
                return "";
            }
        }
    },
    computed: {
        latestDetailedWinner() {
            return this.detailedWinnersData[0] || null;
        }
    }
}
// #endregion

// #region Store List Mixin
const storeListMixin = {
    asyncData() {
        return {
            storeListData: storeList
        }
    }
};

// #endregion

// #region Partners Mixin
const partnersMixin = {
    asyncData() {
        const partnersData = partnersList ? partnersList.sort((a, b) => {
            const targetName = Utils.pageIsFrench() ? "name_fr" : "name";
            return a[targetName].localeCompare(b[targetName]);
        }) : [];
        const availableFilters = [langPartnersFilterAll, "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z"];
        const enabledFilters = partnersData.reduce((acc, curr) => {
            const currName = Utils.pageIsFrench() ? curr.name_fr : curr.name;
            const firstLetter = currName.charAt(0).toUpperCase();
            if (!availableFilters.includes(firstLetter)) return acc;
            if (!acc.includes(firstLetter)) acc.push(firstLetter);
            return acc;
        }, []);

        return {
            partnersData: partnersData,
            availableFilters: availableFilters,
            enabledFilters: enabledFilters,
        }
    },
    data() {
        return {
            currentFilterLetter: null,
        }
    },
    methods: {
        setFilterLetter(letter = null) {
            if (!this.isEnabled(letter)) return;
            this.currentFilterLetter = letter === langPartnersFilterAll ? null : letter;
        },
        isCurrentlySelected(letter) {
            return this.currentFilterLetter === (letter === langPartnersFilterAll ? null : letter);
        },
        isEnabled(letter) {
            return letter === langPartnersFilterAll ? true : this.enabledFilters.includes(letter);
        }
    },
    computed: {
        filteredPartners() {
            if (!this.currentFilterLetter) return this.partnersData;
            const targetName = this.pageIsFrench ? "name_fr" : "name";
            return this.partnersData.filter(partner => partner[targetName].charAt(0).toLowerCase() === this.currentFilterLetter.toLowerCase());
        }
    },
}
// #endregion
